'use client'

import { cn } from '@/lib/utils'

/**
 * Audience-size picker for creators.
 *
 * A slider rather than a dropdown: the question is "roughly how big are you",
 * and a continuous control says "an estimate is fine" where a list of exact
 * numbers would imply we need a precise figure. Index 0 is "just starting",
 * which is a real answer here, not an empty state.
 */

export const AUDIENCE_STEPS = [
  'Dopiero zaczynam',
  'do 1 000',
  '1 – 10 tys.',
  '10 – 50 tys.',
  '50 – 100 tys.',
  '100 – 500 tys.',
  '500 tys.+',
] as const

export function AudienceSlider({
  value,
  onChange,
  id = 'audience',
}: {
  value: number
  onChange: (next: number) => void
  id?: string
}) {
  const max = AUDIENCE_STEPS.length - 1
  const pct = (value / max) * 100

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-baseline justify-between gap-3">
        <label htmlFor={id} className="text-sm text-foreground/80">
          Ilu masz subskrybentów?
        </label>
        <span className="text-[0.95rem] font-semibold text-foreground">
          {AUDIENCE_STEPS[value]}
        </span>
      </div>

      <input
        id={id}
        type="range"
        min={0}
        max={max}
        step={1}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        aria-valuetext={AUDIENCE_STEPS[value]}
        // Track is painted with a gradient stop at the current position so the
        // filled portion reads as progress without a second element.
        style={{
          background: `linear-gradient(to right, oklch(0.66 0.25 320) 0%, oklch(0.72 0.22 350) ${pct}%, rgba(255,255,255,0.14) ${pct}%, rgba(255,255,255,0.14) 100%)`,
        }}
        className={cn(
          'h-2 w-full cursor-pointer appearance-none rounded-full outline-none',
          'focus-visible:ring-2 focus-visible:ring-foreground/40',
          '[&::-webkit-slider-thumb]:h-5 [&::-webkit-slider-thumb]:w-5 [&::-webkit-slider-thumb]:appearance-none',
          '[&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-white',
          '[&::-webkit-slider-thumb]:shadow-[0_2px_8px_rgba(0,0,0,0.4)]',
          '[&::-moz-range-thumb]:h-5 [&::-moz-range-thumb]:w-5 [&::-moz-range-thumb]:rounded-full',
          '[&::-moz-range-thumb]:border-0 [&::-moz-range-thumb]:bg-white',
        )}
      />

      <div className="flex justify-between text-[0.75rem] text-muted-foreground">
        <span>Start</span>
        <span>500 tys.+</span>
      </div>
    </div>
  )
}
